import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ordersService } from '../../services/orders.service';
import { RegisterService } from 'src/app/auth/container/registration-module/register.service';

export interface PodRemark {
  categoryId: number | null;
  quantity: number | null;
  amount: number | null;
  remarks: string;
}

export interface AdditionalCharge {
  chargeTypeId: number | null;
  chargeTypeName: string;
  charge: number | null;
  vendorCharge: number | null;
  chargeToCustomer: boolean;
  chargeToVendor: boolean;
  docUrl: string;
  remarks: string;
  uploading?: boolean;
  saving?: boolean;
}

export type EndTripMode = 'END_TRIP' | 'ADD_POD' | 'SHOW_POD';

@Component({
  selector: 'app-end-trip',
  templateUrl: './end-trip.component.html',
  styleUrls: ['./end-trip.component.css']
})
export class EndTripComponent implements OnInit {

  orderId: any;
  order: any = null;
  tripData: any = null;
  isLoading = false;

  // ── Mode: drives read-only vs editable rendering ──
  // END_TRIP -> full editable flow (trip still in progress)
  // ADD_POD  -> editable POD/charge forms (trip ended, soft POD received, hard POD pending)
  // SHOW_POD -> fully read-only view (hard POD received) — no forms, no add/save buttons
  mode: EndTripMode = 'END_TRIP';

  get isReadOnly(): boolean {
    return this.mode === 'SHOW_POD';
  }

  // ── Section 1: Arrival ──
  arrivalReportingDateTime = '';
  arrivalUnloadingDateTime = '';
  arrivalSaving = false;

  // ── Section 2: POD ──
  savedPods: any[] = [];
  savedPodsLoading = false;

  podCondition: 'CLEAN' | 'REMARK' = 'CLEAN';
  podRemarks: PodRemark[] = [this.emptyRemark()];

  softPodDate = '';
  softPodUrl = '';
  softPodUploading = false;
  podSaving = false;

  hardPodTab: 'PENDING' | 'RECEIVED' | 'LOST' = 'PENDING';
  hardPodMode: number | null = null;
  hardPodSubmissionDate = '';
  hardPodUrl = '';
  hardPodUploading = false;
  hardPodSaving = false;
  indemnityStatus: 'RECEIVED' | 'PENDING' | null = null;

  // ── Section 3: Additional Charges ──
  savedCharges: any[] = [];
  savedChargesLoading = false;
  charges: AdditionalCharge[] = [this.emptyCharge()];

  // ── Dropdowns ──
  chargeTypeList: any[] = [];
  podCategoryList: any[] = [];
  podModeList: any[] = [];

  closingTrip = false;

  private readonly uploadApi = 'https://ftl-dev.apollosupplychain.com/utils-service/uploads/uploadFiles';

  allCatagory: any;
  quickCodeCategoryList: any;
  sendCatagoryId: any;

  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private orderService: ordersService,
    private messageService: MessageService,
    private registerService: RegisterService,
    private http: HttpClient
  ) {
    this.allCatagory = JSON.parse(localStorage.getItem('categoryMap')!);
    this.quickCodeCategoryList = JSON.parse(localStorage.getItem('category')!);
    this.sendCatagoryId = this.setId();
    this.getQuickCodeListByCategoryId(this.sendCatagoryId.toString());
  }

  ngOnInit(): void {
    this.orderId = this.activeRoute.snapshot.params['id'];

    // Read mode from query params (?mode=SHOW_POD / ADD_POD / END_TRIP)
    const modeParam = this.activeRoute.snapshot.queryParams['mode'] as EndTripMode;
    this.mode = modeParam || 'END_TRIP';

    if (this.orderId) {
      this.isLoading = true;
      this.loadData();
    }
    this.loadDropdowns();
  }

  // ─────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────

  loadData(): void {
    this.orderService.getOrderById(this.orderId).subscribe({
      next: (res: any) => {
        this.order = res?.responseObject;

        // If mode wasn't passed explicitly (e.g. deep link / refresh),
        // derive it from the order's own status list so behavior stays
        // consistent with the list screen's End Trip / Add POD / Show POD logic.
        if (!this.activeRoute.snapshot.queryParams['mode']) {
          this.mode = this.deriveModeFromOrder(this.order);
        }

        this.orderService.getOrderTripById(this.orderId).subscribe({
          next: (tripRes: any) => {
            this.isLoading = false;
            this.tripData = tripRes?.responseObject;
            if (this.tripData) {
              this.patchArrivalForm();
              this.hardPodTab = this.tripData?.hardPodStatus || 'PENDING';
            }
            this.loadSavedPods();
            this.loadSavedCharges();
          },
          error: () => { this.isLoading = false; }
        });
      },
      error: () => { this.isLoading = false; }
    });
  }

  deriveModeFromOrder(order: any): EndTripMode {
    const tripEnded = this.checkTripStatus(order, 'TRIP_ENDED');
    const softReceived = this.checkSoftPod(order, 'RECEIVED');
    const hardPending = this.checkHardPod(order, 'PENDING');
    const hardReceived = this.checkHardPod(order, 'RECEIVED');

    if (tripEnded && hardReceived) return 'SHOW_POD';
    if (tripEnded && softReceived && hardPending) return 'ADD_POD';
    return 'END_TRIP';
  }

  checkTripStatus(item: any, status: any): boolean {
    return item?.orderStatus?.some(
      (el: any) => el.statusType === 'TRIP_STATUS' && el.status === status
    ) || false;
  }
  checkHardPod(item: any, status: any): boolean {
    return item?.orderStatus?.some(
      (el: any) => el.statusType === 'HARD_POD_STATUS' && el.status === status
    ) || false;
  }
  checkSoftPod(item: any, status: any): boolean {
    return item?.orderStatus?.some(
      (el: any) => el.statusType === 'SOFT_POD_STATUS' && el.status === status
    ) || false;
  }

  patchArrivalForm(): void {
    this.arrivalReportingDateTime = this.toLocalDatetime(this.tripData?.arrivalReportingDateTime || this.tripData?.reportingDateTime);
    this.arrivalUnloadingDateTime = this.toLocalDatetime(this.tripData?.unloadingDateTime || this.tripData?.loadingDateTime);
  }

  setId(): (number | undefined)[] {
    const keys = [
      'additionalCharges',
      'podCategory',
      'mode'
    ];
    return keys.map((key: any) => this.allCatagory?.[key]?.id);
  }

  getQuickCodeListByCategoryId(ids: any) {
    this.registerService
      .getQuickCodeListByCategoryId(ids)
      .subscribe((data: any) => {
        this.chargeTypeList = data?.responseObject?.additionalCharges;
        this.podCategoryList = data?.responseObject?.podCategory;
        this.podModeList = data?.responseObject?.mode;
      });
  }

  loadDropdowns(): void {
    // this.orderService.getChargeTypes([],0,1000).subscribe({
    //   next: (res: any) => { this.chargeTypeList = res?.responseObject?.content || res?.responseObject || []; },
    //   error: () => {}
    // });
    // this.orderService.getPodCategories([],0,1000).subscribe({
    //   next: (res: any) => { this.podCategoryList = res?.responseObject?.content || res?.responseObject || []; },
    //   error: () => {}
    // });
    // this.orderService.getPodModes([],0,1000).subscribe({
    //   next: (res: any) => { this.podModeList = res?.responseObject?.content || res?.responseObject || []; },
    //   error: () => {}
    // });
  }

  loadSavedPods(): void {
    if (!this.tripData?.id) return;
    const filters = [
      { key: 'TRIP_ID_IN', value: this.tripData.id.toString() },
      { key: 'ORDER_ID_IN', value: (this.order?.id ?? this.orderId)?.toString() }
    ];
    this.savedPodsLoading = true;
    this.orderService.getPods(filters, 0, 10000).subscribe({
      next: (res: any) => {
        this.savedPodsLoading = false;
        this.savedPods = res?.responseObject?.content || res?.responseObject || [];
      },
      error: () => { this.savedPodsLoading = false; }
    });
  }

  loadSavedCharges(): void {
    if (!this.tripData?.id) return;
    const filters = [
      { key: 'TRIP_ID_IN', value: this.tripData.id.toString() },
      { key: 'ORDER_ID_IN', value: (this.order?.id ?? this.orderId)?.toString() }
    ];
    this.savedChargesLoading = true;
    this.orderService.getAdditionalCharges(filters, 0, 20).subscribe({
      next: (res: any) => {
        this.savedChargesLoading = false;
        const list = res?.responseObject?.content || res?.responseObject || [];
        // API returns the field as `chargeType`, not `chargeTypeName` —
        // normalize here so the template can rely on one field name.
        this.savedCharges = list.map((c: any) => ({
          ...c,
          chargeTypeName: c.chargeType || c.chargeTypeName || ''
        }));
      },
      error: () => { this.savedChargesLoading = false; }
    });
  }

  // ─────────────────────────────────────────────
  // DAYS CALCULATION
  // ─────────────────────────────────────────────

  get tripDays(): number {
    if (!this.arrivalReportingDateTime || !this.arrivalUnloadingDateTime) return 0;
    const start = new Date(this.arrivalReportingDateTime).getTime();
    const end   = new Date(this.arrivalUnloadingDateTime).getTime();
    if (isNaN(start) || isNaN(end)) return 0;
    return Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
  }

  get tripDaysLabel(): string {
    const d = this.tripDays;
    if (d === 0) return 'Same day delivery';
    return d === 1 ? '1 day transit' : `${d} days transit`;
  }

  // ─────────────────────────────────────────────
  // SECTION 2: POD
  // ─────────────────────────────────────────────

  emptyRemark(): PodRemark {
    return { categoryId: null, quantity: null, amount: null, remarks: '' };
  }

  addRemark(): void { this.podRemarks.push(this.emptyRemark()); }

  removeRemark(i: number): void {
    if (this.podRemarks.length > 1) this.podRemarks.splice(i, 1);
  }

  onSoftPodFileSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    this.softPodUploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => { this.softPodUploading = false; this.softPodUrl = url; },
      error: () => {
        this.softPodUploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed' });
      }
    });
  }

  savePodDetails(): void {
    if (this.isReadOnly) return;

    if (!this.softPodDate) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'POD date is required' });
      return;
    }

    const lineItems = this.podCondition === 'REMARK'
      ? this.podRemarks
          .filter(r => r.categoryId)
          .map(r => ({
            categoryId: r.categoryId,
            quantity:   r.quantity,
            amount:     r.amount,
            remarks:    r.remarks
          }))
      : [];

    const payload: any = {
      orderId:    this.order?.id,
      accountId:  this.order?.accountId || this.tripData?.accountId,
      customerId: this.order?.customerId || this.tripData?.customerId,
      tripId:     this.tripData?.id,
      vendorId:   this.tripData?.vendorId || this.order?.vendorId,
      podType:    'Soft',
      podUrl:     this.softPodUrl || '',
      podDate:    this.toApiDatetime(this.softPodDate),
      isClean:    this.podCondition === 'CLEAN' ? 1 : 0,
      status:     'RECEIVED',
      lineItems
    };

    this.podSaving = true;
    this.orderService.savePod(payload).subscribe({
      next: (res: any) => {
        this.podSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'POD details saved successfully' });
        this.loadSavedPods();
      },
      error: (err: any) => {
        this.podSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save POD' });
      }
    });
  }

  onHardPodFileSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    this.hardPodUploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => { this.hardPodUploading = false; this.hardPodUrl = url; },
      error: () => {
        this.hardPodUploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed' });
      }
    });
  }

  saveHardPod(): void {
    if (this.isReadOnly) return;

    const payload: any = {
      orderId:    this.order?.id,
      tripId:     this.tripData?.id,
      accountId:  this.order?.accountId || this.tripData?.accountId,
      customerId: this.order?.customerId || this.tripData?.customerId,
      vendorId:   this.tripData?.vendorId || this.order?.vendorId,
      podType:    'Hard',
      status:     this.hardPodTab
    };

    if (this.hardPodTab === 'PENDING') {
      // Just save status as PENDING — no extra fields needed
    }

    if (this.hardPodTab === 'RECEIVED') {
      if (!this.hardPodSubmissionDate) {
        this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Submission date is required' });
        return;
      }
      payload.podUrl  = this.hardPodUrl || '';
      payload.podDate = this.toApiDatetime(this.hardPodSubmissionDate);
      payload.modeId  = this.hardPodMode;
    }

    if (this.hardPodTab === 'LOST') {
      if (!this.indemnityStatus) {
        this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Select indemnity status' });
        return;
      }
      payload.indemnityStatus = this.indemnityStatus;
    }

    this.hardPodSaving = true;
    this.orderService.savePod(payload).subscribe({
      next: (res: any) => {
        this.hardPodSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Hard POD saved successfully' });
        this.loadSavedPods();
      },
      error: (err: any) => {
        this.hardPodSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save hard POD' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // SECTION 3: ADDITIONAL CHARGES
  // ─────────────────────────────────────────────

  emptyCharge(): AdditionalCharge {
    return {
      chargeTypeId: null,
      chargeTypeName: '',
      charge: null,
      vendorCharge: null,
      chargeToCustomer: false,
      chargeToVendor: false,
      docUrl: '',
      remarks: ''
    };
  }

  onChargeTypeChange(charge: AdditionalCharge, event: any): void {
    const found = this.chargeTypeList.find(c => c.id === charge.chargeTypeId);
    charge.chargeTypeName = found?.name || found?.chargeName || '';
  }

  onChargeFileSelected(event: any, charge: AdditionalCharge): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    charge.uploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => { charge.uploading = false; charge.docUrl = url; },
      error: () => {
        charge.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed' });
      }
    });
  }

  saveCharge(charge: AdditionalCharge): void {
    if (this.isReadOnly) return;

    if (!charge.chargeTypeId || !charge.charge) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Type and amount are required' });
      return;
    }
    if (!charge.chargeToCustomer && !charge.chargeToVendor) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Select at least one charge allocation' });
      return;
    }

    const payload: any = {
      orderId:         this.order?.id,
      tripId:          this.tripData?.id,
      accountId:       this.order?.accountId || this.tripData?.accountId,
      chargeTypeId:    charge.chargeTypeId,
      chargeTypeName:  charge.chargeTypeName,
      chargeToCustomer: charge.chargeToCustomer,
      chargeToVendor:  charge.chargeToVendor,
      charge:          charge.charge,
      vendorCharge:    charge.chargeToVendor ? (charge.vendorCharge || 0) : undefined,
      docUrl:          charge.docUrl || undefined,
      remarks:         charge.remarks || undefined
    };

    charge.saving = true;
    this.orderService.saveAdditionalCharge(payload).subscribe({
      next: (res: any) => {
        charge.saving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Charge added successfully' });
        this.charges.push(this.emptyCharge()); // Add new blank row
        this.loadSavedCharges();
      },
      error: (err: any) => {
        charge.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save charge' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // CLOSE TRIP
  // ─────────────────────────────────────────────

  canCloseTrip(): boolean {
    return this.savedPods.length > 0;
  }

  closeTrip(): void {
    if (this.isReadOnly) return;

    if (!this.canCloseTrip()) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'At least one saved POD is required to close the trip' });
      return;
    }

    const payload: any = {
      id:              this.tripData?.id,
      orderId:         this.order?.id,
      accountId:       this.order?.accountId || this.tripData?.accountId,
      customerId:      this.order?.customerId || this.tripData?.customerId,
      status:          'TRIP_ENDED',
      reportingDateTime: this.toApiDatetime(this.arrivalReportingDateTime),
      loadingDateTime:   this.toApiDatetime(this.arrivalUnloadingDateTime)
    };

    this.closingTrip = true;
    this.orderService.saveTrip(payload).subscribe({
      next: (res: any) => {
        this.closingTrip = false;
        this.messageService.add({ severity: 'success', summary: 'Trip Closed', detail: 'Trip has been ended successfully' });
        this.router.navigate(['/orders/trip']);
      },
      error: (err: any) => {
        this.closingTrip = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to close trip' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // FILE UPLOAD
  // ─────────────────────────────────────────────

  uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(this.uploadApi, formData).pipe(
      map((res: any) =>
        res?.responseObject?.url || res?.responseObject?.fileUrl ||
        (typeof res?.responseObject === 'string' ? res.responseObject : '') ||
        res?.url || res?.fileUrl || ''
      )
    );
  }

  // ─────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────

  toLocalDatetime(apiDate: string | undefined): string {
    if (!apiDate) return '';
    const match = apiDate.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})/);
    if (!match) return '';
    const [, dd, mm, yyyy, hh, min] = match;
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  toApiDatetime(localDt: string): string {
    if (!localDt) return '';
    const [datePart, timePart] = localDt.split('T');
    if (!datePart || !timePart) return '';
    const [yyyy, mm, dd] = datePart.split('-');
    return `${dd}-${mm}-${yyyy} ${timePart}:00 +0530`;
  }

  formatApiDate(raw: string | undefined): string {
    if (!raw) return '--';
    const match = raw.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (!match) return raw;
    return `${match[1]}/${match[2]}/${match[3]}`;
  }

  podStatusClass(status: string): string {
    if (status === 'RECEIVED') return 'badge-green';
    if (status === 'LOST') return 'badge-red';
    return 'badge-yellow';
  }

  categoryName(id: number | null | undefined): string {
    if (!id) return '—';
    const c = this.podCategoryList?.find((x: any) => x.id === id);
    return c?.name || c?.categoryName || '—';
  }

  modeName(id: number | null | undefined): string {
    if (!id) return '—';
    const m = this.podModeList?.find((x: any) => x.id === id);
    return m?.name || m?.modeName || '—';
  }

  goBack(): void {
    this.router.navigate(['/orders/trip']);
  }
}